/**
 * Room timeline pagination, hydration after sync, and debug helpers.
 */

import { captureChatException, isMatrixExpectedError } from 'shared/sentry';
import { isAbortError } from '../../utils/isAbortError.js';
import { paginateLiveTimelineBackwards } from '../../utils/matrix/matrixChatPagination';
import {
  HYDRATE_BATCH_JITTER_MAX_MS,
  HYDRATE_PAGINATE_TIMEOUT_MS,
  HYDRATE_ROOMS_CONCURRENCY,
  HYDRATE_ROOMS_MAX,
  MATRIX_POST_SYNC_HYDRATION_LIMIT,
} from './matrixConstants';
import { isSameMatrixClient, withTimeout } from './matrixUtils';

/**
 * Back-fill the live timeline when /sync returned a limited window (or the timeline is still sparse).
 * @param {import('matrix-js-sdk').MatrixClient} client
 * @param {import('matrix-js-sdk').Room} room
 * @param {number} [limit]
 * @param {import('./matrixUtils').MatrixService|null} [service] - when set, abort if `service.matrixClient` no longer equals `client`
 */
export async function paginateRoomIfNeeded(client, room, limit = 30, service = null) {
  if (!room || !client) return;

  const clientRef = client;
  const stale = () => service != null && !isSameMatrixClient(service, clientRef);

  const timeline = room.getLiveTimeline?.();
  if (!timeline) return;

  const safeLimit = typeof limit === 'number' && limit > 0 ? Math.floor(limit) : 30;
  const targetCount = safeLimit * 2;
  const maxPasses = 10;

  for (let pass = 0; pass < maxPasses; pass += 1) {
    if (stale()) return;
    const eventCount = timeline.getEvents?.()?.length ?? 0;
    if (eventCount >= targetCount) break;

    const result = await paginateLiveTimelineBackwards(client, room, Math.max(20, safeLimit));
    if (stale()) return;

    if (result.error) break;
    if (!result.progressed) break;
  }
}

/**
 * When opening a chat, ensure the live timeline is not empty so first paint can start.
 * Deeper history is loaded by the chat hook in message-sized pages.
 * @param {import('matrix-js-sdk').MatrixClient} client
 * @param {import('matrix-js-sdk').Room} room
 * @param {import('./matrixUtils').MatrixService|null} [service]
 */
export async function ensureRoomReady(client, room, service = null) {
  if (!room || !client) return;
  const timeline = room.getLiveTimeline?.();
  const events = timeline?.getEvents?.() || [];
  if (events.length < 5) {
    await paginateRoomIfNeeded(client, room, 15, service);
  }
}

/**
 * After /sync the live timeline can still be thin (restart, limited sync, VoIP). Prime each room once.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {number} [limit]
 * @param {number|null} [generation]
 */
export async function hydrateRoomTimelinesAfterSync(service, limit = MATRIX_POST_SYNC_HYDRATION_LIMIT, generation = null) {
  if (!service.matrixClient) return;
  const client = service.matrixClient;
  try {
    const rooms = service.matrixClient.getRooms?.() || [];
    if (!isSameMatrixClient(service, client)) return;
    const sorted = [...rooms].sort((a, b) => {
      const ta = typeof a?.getLastActiveTimestamp === 'function' ? a.getLastActiveTimestamp() : 0;
      const tb = typeof b?.getLastActiveTimestamp === 'function' ? b.getLastActiveTimestamp() : 0;
      return tb - ta;
    });
    const cores = typeof navigator !== 'undefined' ? navigator?.hardwareConcurrency : null;
    const isLowEndDevice = typeof cores === 'number' && cores > 0 ? cores <= 4 : false;
    const hydrateRoomsMax = isLowEndDevice ? 10 : HYDRATE_ROOMS_MAX;
    const hydrateConcurrency = isLowEndDevice ? 2 : HYDRATE_ROOMS_CONCURRENCY;

    const toHydrate = sorted.slice(0, hydrateRoomsMax);
    const batch = Math.max(1, hydrateConcurrency);
    for (let i = 0; i < toHydrate.length; i += batch) {
      if (generation != null && generation !== service.initGeneration) return;
      if (!isSameMatrixClient(service, client)) return;
      const chunk = toHydrate.slice(i, i + batch);
      if (HYDRATE_BATCH_JITTER_MAX_MS > 0) {
        await new Promise((r) => setTimeout(r, Math.random() * HYDRATE_BATCH_JITTER_MAX_MS));
      }
      if (!isSameMatrixClient(service, client)) return;
      await Promise.all(
        chunk.map((room) =>
          withTimeout(
            paginateRoomIfNeeded(client, room, limit, service),
            HYDRATE_PAGINATE_TIMEOUT_MS,
            'hydrate-paginate-timeout',
          ).catch((e) => {
            if (!isAbortError(e) && !isMatrixExpectedError(e)) {
              captureChatException(e, {
                tags: { matrix: 'timeline', action: 'hydrate-room' },
                extra: { roomId: room?.roomId },
              });
            }
          }),
        ),
      );
    }
  } catch (e) {
    if (!isAbortError(e) && !isMatrixExpectedError(e)) {
      captureChatException(e, { tags: { matrix: 'timeline', action: 'hydrate-after-sync' } });
    }
  }
}

/**
 * Development helper: room count + per-room live timeline size after sync/hydration.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {string} [context]
 */
export function logTimelineDebug(service, context = 'sync') {
  if (process.env.NODE_ENV !== 'development') return;
  try {
    const rooms = service.matrixClient?.getRooms?.() || [];
    // Dev-only local log — never open a Sentry issue for routine timeline sizing.
    // eslint-disable-next-line no-console
    console.debug(`[matrix timeline] ${context}: ${rooms.length} rooms`, rooms.map((r) => ({
      roomId: r.roomId,
      events: r?.getLiveTimeline?.()?.getEvents?.()?.length ?? 0,
    })));
  } catch (_) {
    // ignore debug failures
  }
}

/**
 * Run post-sync hydration without blocking initialize()/restartSync.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {Object} [opts]
 */
export function schedulePostSyncTimelineHydration(
  service,
  {
    limit = MATRIX_POST_SYNC_HYDRATION_LIMIT,
    generation = null,
    context = 'hydrate',
  } = {},
) {
  const clientAtSchedule = service.matrixClient;
  void (async () => {
    try {
      await hydrateRoomTimelinesAfterSync(service, limit, generation);
      if (generation != null && generation !== service.initGeneration) return;
      if (!isSameMatrixClient(service, clientAtSchedule)) return;
      logTimelineDebug(service, context);
    } catch (e) {
      if (!isAbortError(e) && !isMatrixExpectedError(e)) {
        captureChatException(e, { tags: { matrix: 'timeline', action: 'post-sync-hydrate' } });
      }
    }
  })();
}
